import { formatSiteDateTime } from "@/lib/site-time";
/**
 * Interne console met de dagelijkse paginascan (PageSpeed Insights / Lighthouse).
 *
 * Toont per pagina de vier Lighthouse-scores, het verschil met de vorige scan,
 * de labmetingen en het concrete verbeteradvies. Interne pagina: NL-only.
 */
/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { getPageAuditDashboard, runPageAuditNow, type AuditPage } from "@/lib/page-audit.functions";
import {
  AUDIT_STRATEGIES,
  FIELD_METRICS,
  RATING_LABELS,
  SCORE_KEYS,
  SCORE_LABELS,
  adviceFor,
  formatSavings,
  pageLabel,
  rateFieldMetric,
  rateScore,
  type AuditStrategy,
  type FieldMetricKey,
  type Opportunity,
  type ScoreKey,
  type ScoreRating,
  type FieldRating,
} from "@/lib/page-audit";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/pagespeed")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Paginascan | Beheer Gopro Projects" },
      { name: "description", content: "Dagelijkse Lighthouse-scan met verbeteradvies per pagina." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Paginascan | Beheer Gopro Projects" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:description", content: "Dagelijkse Lighthouse-scan met verbeteradvies." },
    ],
  }),
  component: PageSpeedConsole,
});

const ratingClass: Record<ScoreRating, string> = {
  excellent: "bg-emerald-100 text-emerald-900 border-emerald-300",
  very_good: "bg-emerald-50 text-emerald-800 border-emerald-200",
  good: "bg-lime-50 text-lime-800 border-lime-200",
  moderate: "bg-amber-100 text-amber-800 border-amber-200",
  poor: "bg-destructive/10 text-destructive border-destructive/30",
  very_poor: "bg-destructive/20 text-destructive border-destructive/50",
};

const fieldRatingClass: Record<FieldRating, string> = {
  good: ratingClass.very_good,
  average: ratingClass.moderate,
  poor: ratingClass.poor,
};

function ScorePill({ value, showLabel = false }: { value: number | null; showLabel?: boolean }) {
  const rating = rateScore(value);
  if (value === null || !rating)
    return <span className="text-muted-foreground text-sm">&mdash;</span>;
  return (
    <span
      className={`inline-flex min-w-11 justify-center gap-1 rounded-md border px-2 py-0.5 text-sm font-semibold tabular-nums ${ratingClass[rating]}`}
      title={RATING_LABELS[rating]}
    >
      {value}
      {showLabel ? <span className="font-medium">· {RATING_LABELS[rating]}</span> : null}
    </span>
  );
}

function Delta({ now, before }: { now: number | null; before: number | null }) {
  if (now === null || before === null) return null;
  const diff = now - before;
  if (diff === 0) return null;
  const up = diff > 0;
  return (
    <span
      className={`ml-1 text-xs tabular-nums ${up ? "text-emerald-700" : "text-destructive"}`}
      title="Verschil met vorige scan"
    >
      {up ? "+" : ""}
      {diff}
    </span>
  );
}

function fmtField(key: FieldMetricKey, value: number | null): string {
  if (value === null) return "—";
  if (key === "cls") return value.toFixed(3);
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${value} ms`;
}

function PageSpeedConsole() {
  const [strategy, setStrategy] = useState<AuditStrategy>("mobile");
  const [openPath, setOpenPath] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const fetchDashboard = useServerFn(getPageAuditDashboard);
  const startScan = useServerFn(runPageAuditNow);

  const { data, isLoading, error } = useQuery({
    queryKey: ["page-audit", strategy],
    queryFn: () => fetchDashboard({ data: { strategy } }),
  });

  const scan = useMutation({
    mutationFn: () => startScan({ data: { strategy } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["page-audit"] }),
  });

  const previousByPath = useMemo(() => {
    const map = new Map<string, AuditPage>();
    for (const p of data?.previousPages ?? []) map.set(p.path, p);
    return map;
  }, [data]);

  /** Sitebrede top: welk advies keert op de meeste pagina's terug? */
  const topAdvice = useMemo(() => {
    const map = new Map<string, { op: Opportunity; pages: string[]; savings: number }>();
    for (const page of data?.pages ?? []) {
      for (const op of page.opportunities ?? []) {
        const entry = map.get(op.id) ?? { op, pages: [], savings: 0 };
        entry.pages.push(page.path);
        entry.savings += op.savingsMs ?? 0;
        map.set(op.id, entry);
      }
    }
    return [...map.values()]
      .sort((a, b) => b.pages.length - a.pages.length || b.savings - a.savings)
      .slice(0, 8);
  }, [data]);

  const averages = useMemo(() => {
    const pages = data?.pages ?? [];
    const avg = (key: ScoreKey) => {
      const values = pages.map((p) => p[key]).filter((v): v is number => typeof v === "number");
      if (!values.length) return null;
      return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    };
    return SCORE_KEYS.map((key) => ({ key, value: avg(key) }));
  }, [data]);

  const latest = data?.latestRun ?? null;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10">
      <AdminNav />

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold">Paginascan</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            Dagelijkse Lighthouse-meting van de belangrijkste pagina&apos;s, met advies per punt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="border-border flex rounded-md border p-0.5">
            {AUDIT_STRATEGIES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStrategy(s)}
                className={`rounded px-3 py-1 text-sm capitalize ${
                  strategy === s ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <Button onClick={() => scan.mutate()} disabled={scan.isPending}>
            {scan.isPending ? "Bezig met scannen…" : "Scan nu"}
          </Button>
        </div>
      </header>

      {latest ? (
        <p className="text-muted-foreground mb-6 text-sm">
          Laatste scan: {formatSiteDateTime(latest.started_at)} &middot;{" "}
          {latest.pages_ok} ok, {latest.pages_failed} mislukt &middot; {latest.trigger_source} &middot; {latest.status === "done" ? "Voltooid" : latest.status === "running" ? "Bezig" : "Mislukt"}
        </p>
      ) : null}

      {latest?.error ? <p className="text-destructive mb-4 text-sm">{latest.error}</p> : null}

      {error ? (
        <p className="text-destructive text-sm">{(error as Error).message}</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">Laden…</p>
      ) : !data?.pages.length ? (
        <div className="border-border rounded-lg border p-6">
          <p className="text-sm">
            Nog geen scanresultaten. Klik op &laquo;Scan nu&raquo; of wacht op de dagelijkse cron.
          </p>
        </div>
      ) : (
        <>
          <section className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {averages.map(({ key, value }) => (
              <div key={key} className="border-border rounded-lg border p-4">
                <p className="text-muted-foreground text-xs uppercase">{SCORE_LABELS[key]}</p>
                <div className="mt-2">
                  <ScorePill value={value} showLabel />
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Gemiddelde van alle {data.pages.length} pagina&apos;s
                </p>
              </div>
            ))}
          </section>

          <section className="mb-8">
            <h2 className="font-display mb-3 text-xl font-semibold">Belangrijkste adviezen</h2>
            <ul className="space-y-2">
              {topAdvice.map(({ op, pages, savings }) => (
                <li key={op.id} className="border-border rounded-lg border p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {op.category}
                    </Badge>
                    <span className="font-medium">{adviceFor(op)}</span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {pages.length} pagina&apos;s
                    {savings >= 50 ? ` · ~${Math.round(savings)} ms totale winst` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display mb-3 text-xl font-semibold">Per pagina</h2>
            <div className="border-border overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2">Pagina</th>
                    {SCORE_KEYS.map((key) => (
                      <th key={key} className="px-3 py-2">
                        {SCORE_LABELS[key]}
                      </th>
                    ))}
                    <th className="px-3 py-2">LCP</th>
                    <th className="px-3 py-2">CLS</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.pages.map((page) => {
                    const before = previousByPath.get(page.path);
                    const open = openPath === page.path;
                    return (
                      <Fragment key={page.path}>
                        <tr className="border-border border-t">
                          <td className="px-3 py-2">
                            <span className="font-medium">{pageLabel(page.path)}</span>
                            <span className="text-muted-foreground ml-2 text-xs">{page.path}</span>
                          </td>
                          {SCORE_KEYS.map((key) => (
                            <td key={key} className="px-3 py-2">
                              <ScorePill value={page[key]} showLabel />
                              <Delta now={page[key]} before={before?.[key] ?? null} />
                            </td>
                          ))}
                          <td className="px-3 py-2 tabular-nums">
                            {fmtField("lcp_ms", page.lcp_ms)}
                          </td>
                          <td className="px-3 py-2 tabular-nums">{fmtField("cls", page.cls)}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              className="text-primary text-xs underline"
                              onClick={() => setOpenPath(open ? null : page.path)}
                            >
                              {open ? "Sluiten" : "Advies"}
                            </button>
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-border border-t">
                            <td colSpan={8} className="bg-muted/30 px-3 py-4">
                              {page.error ? (
                                <p className="text-destructive text-sm">{page.error}</p>
                              ) : null}
                              <div className="mb-4 flex flex-wrap gap-3">
                                {FIELD_METRICS.map((m) => {
                                  const value = page[m.key] as number | null;
                                  const rating = rateFieldMetric(m.key, value);
                                  return (
                                    <span
                                      key={m.key}
                                      className={`rounded-md border px-2 py-1 text-xs ${
                                        rating ? fieldRatingClass[rating] : "border-border"
                                      }`}
                                    >
                                      {m.label}: {fmtField(m.key, value)}
                                    </span>
                                  );
                                })}
                              </div>
                              <ul className="space-y-2">
                                {(page.opportunities ?? []).map((op) => (
                                  <li key={op.id} className="text-sm">
                                    <span className="font-medium">{adviceFor(op)}</span>
                                    <span className="text-muted-foreground">
                                      {" "}
                                      ({op.category}
                                      {formatSavings(op) ? `, ${formatSavings(op)}` : ""})
                                    </span>
                                  </li>
                                ))}
                                {!(page.opportunities ?? []).length ? (
                                  <li className="text-muted-foreground text-sm">
                                    Geen openstaande adviezen voor deze pagina.
                                  </li>
                                ) : null}
                              </ul>
                              <a
                                className="text-primary mt-3 inline-block text-xs underline"
                                href={`https://pagespeed.web.dev/report?url=${encodeURIComponent(page.url)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Volledig rapport op pagespeed.web.dev
                              </a>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
